import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { HowToReg, Close } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { tokens } from "../../../../theme";
import { BaseURL } from "../../../../service/Api";
import { fetchSalesReps, SalesRep } from "./salesRepStorage";

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (salesRepId: number) => Promise<void> | void;
}

const getInitials = (name: string) =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

const getMediaUrl = (path?: string | null) => {
  if (!path) return undefined;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  const cleanBaseUrl = BaseURL.replace(/\/$/, "");
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${cleanBaseUrl}${cleanPath}`;
};

export default function AssignSalesRepModal({ open, onClose, onSubmit }: Props) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [reps, setReps] = useState<SalesRep[]>([]);
  const [loadingReps, setLoadingReps] = useState(false);
  const [selectedRepId, setSelectedRepId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setSelectedRepId(null);
      return;
    }

    const load = async () => {
      try {
        setLoadingReps(true);
        setReps(await fetchSalesReps());
      } catch {
        setReps([]);
      } finally {
        setLoadingReps(false);
      }
    };
    load();
  }, [open]);

  const handleSubmit = async () => {
    if (selectedRepId === null) return;
    setSubmitting(true);
    try {
      await onSubmit(selectedRepId);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 4, maxWidth: 460, p: 1 } }}
    >
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          top: 12,
          right: 12,
          color: colors.primary[300],
        }}
      >
        <Close fontSize="small" />
      </IconButton>

      <DialogTitle sx={{ fontWeight: 600, fontSize: 18, pb: 2 }}>
        Assign Sales Rep
      </DialogTitle>

      <DialogContent sx={{ pt: 1, pb: 2 }}>
        <Typography
          sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary", mb: 2 }}
        >
          Select a sales rep
        </Typography>

        {loadingReps ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        ) : reps.length === 0 ? (
          <Typography
            sx={{
              fontSize: 13,
              color: "text.secondary",
              mb: 2,
              p: 1.5,
              bgcolor: colors.primary[800],
              borderRadius: 2,
              textAlign: "center",
            }}
          >
            No users in the Sales Rep group yet.
          </Typography>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2.5,
              mb: 3,
              justifyContent: "flex-start",
            }}
          >
            {reps.map((rep) => {
              const isSelected = selectedRepId === rep.id;
              return (
                <Box
                  key={rep.id}
                  onClick={() => setSelectedRepId(rep.id)}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 0.75,
                    width: 76,
                    cursor: "pointer",
                  }}
                >
                  <Avatar
                    src={getMediaUrl(rep.profile_picture_url)}
                    sx={{
                      width: 60,
                      height: 60,
                      fontSize: 18,
                      fontWeight: 700,
                      bgcolor: colors.primary[600],
                      border: "3px solid",
                      borderColor: isSelected ? "#724E41" : "transparent",
                      transition: "border-color 0.15s ease",
                    }}
                  >
                    {getInitials(rep.full_name)}
                  </Avatar>
                  <Typography
                    noWrap
                    sx={{
                      fontSize: 12,
                      fontWeight: isSelected ? 700 : 500,
                      color: isSelected ? "#724E41" : "text.secondary",
                      maxWidth: "100%",
                    }}
                  >
                    {rep.full_name}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}

        <Button
          variant="contained"
          fullWidth
          disabled={selectedRepId === null || submitting}
          onClick={handleSubmit}
          startIcon={<HowToReg />}
          sx={{
            bgcolor: "#724E41",
            color: "white",
            textTransform: "none",
            borderRadius: 2,
            py: 1.2,
            fontSize: 14,
            fontWeight: 500,
            boxShadow: "none",
            "&:hover": { bgcolor: "#5d3e33", boxShadow: "none" },
          }}
        >
          {submitting ? "Assigning…" : "Assign"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
